import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { Arrival, Departure, EventDto, EventPartialDto, WWRegistrationDto } from "../../../types/eventTypes";
import dayjs from "dayjs";
import NumberField from "../../general/NumberField";

/**
 * Interface for the props of the WorkingWeekendSignUpDialog
 */
interface WorkingWeekendSignUpDialogProps {
  open: boolean;
  handleClose: () => void;
  ww: EventPartialDto | EventDto | null;
  onSubmit: (eventId: number, memberId: number, wwRegistration: WWRegistrationDto) => Promise<void>;
  memberId: number;
}

/**
 * Component to display a dialog to sign up for a working weekend
 * @param open - boolean to determine if the dialog is open
 * @param handleClose - function to close the dialog
 * @param ww - working weekend event to sign up for
 * @param onSubmit - function to submit the sign up
 * @returns The Dialog for the Working Weekend registration
 */
const WorkingWeekendSignUpDialog: React.FunctionComponent<WorkingWeekendSignUpDialogProps> = ({
  open,
  handleClose,
  ww,
  onSubmit,
  memberId,
}: WorkingWeekendSignUpDialogProps) => {
  const [arrival, setArrival] = useState<Arrival | undefined>(undefined);
  const [departure, setDeparture] = useState<Departure | undefined>(undefined);
  const [car, setCar] = useState<boolean>(false);
  const [seats, setSeats] = useState<number>(0);
  const [vegetarian, setVegetarian] = useState<boolean>(false);
  const [remarks, setRemarks] = useState<string>("");
  const [debitNotice, setDebitNotice] = useState<boolean>(false);
  const [errorArrival, setErrorArrival] = useState<boolean>(false);
  const [errorDeparture, setErrorDeparture] = useState<boolean>(false);
  const [errorSeats, setErrorSeats] = useState<boolean>(false);
  const [errorArrivalAfterDeparture, setErrorArrivalAfterDeparture] = useState<boolean>(false);
  const [errorDepartureBeforeArrival, setErrorDepartureBeforeArrival] = useState<boolean>(false);
  const [errorDebitNotice, setErrorDebitNotice] = useState<boolean>(false);

  // Function to reset all values and close the dialog
  const handleCancel = () => {
    handleClose();
    setArrival(undefined);
    setDeparture(undefined);
    setCar(false);
    setSeats(0);
    setVegetarian(false);
    setRemarks("");
    setDebitNotice(false);
    setErrorArrival(false);
    setErrorDeparture(false);
    setErrorSeats(false);
    setErrorArrivalAfterDeparture(false);
    setErrorDepartureBeforeArrival(false);
    setErrorDebitNotice(false);
  };

  /**
   * Function to check if the given arrival and departure times for a working weekend registration are valid, throws an error if not
   * @param arrival The arrival time to check
   * @param departure The departure time to check
   * @return true if the arrival and departure times are valid, false otherwise
   */
  const checkWWArrivalAndDeparture = (arrival: Arrival | undefined, departure: Departure | undefined) => {
    if (arrival === undefined || departure === undefined) {
      return true;
    }
    const arrivalOrder = [
      "Freitag früh",
      "Freitag vor dem Mittagessen",
      "Freitag vor dem Abendessen",
      "Samstag vor dem Frühstück",
      "Samstag vor dem Mittagessen",
      "Samstag vor dem Abendessen",
      "Samstag spät",
    ];
    const departureOrder = [
      "Freitag nach dem Mittagessen",
      "Freitag nach dem Abendessen",
      "Samstag nach dem Frühstück",
      "Samstag nach dem Mittagessen",
      "Samstag nach dem Abendessen",
      "Sonntag",
    ];
    if (arrivalOrder.indexOf(arrival) > departureOrder.indexOf(departure)) {
      return false;
    }
    return true;
  };

  // Function to submit the sign up
  const handleSubmit = () => {
    if (!ww) {
      return;
    }
    // Checks if all required fields are filled out
    if (!arrival || !departure || seats > 9 || !checkWWArrivalAndDeparture(arrival, departure) || !debitNotice) {
      // Reset all error states
      setErrorArrival(false);
      setErrorDeparture(false);
      setErrorSeats(false);
      setErrorArrivalAfterDeparture(false);
      setErrorDepartureBeforeArrival(false);
      setErrorDebitNotice(false);
      if (!arrival) {
        setErrorArrival(true);
      }
      if (!departure) {
        setErrorDeparture(true);
      }
      if (seats > 9) {
        setErrorSeats(true);
      }
      if (!checkWWArrivalAndDeparture(arrival, departure)) {
        setErrorArrivalAfterDeparture(true);
        setErrorDepartureBeforeArrival(true);
      }
      if (!debitNotice) {
        setErrorDebitNotice(true);
      }
      return;
    }
    const wwRegistration: WWRegistrationDto = {
      arrival: arrival,
      departure: departure,
      car,
      seats,
      isVegetarian: vegetarian,
      comment: remarks,
    };
    onSubmit(ww.eventId, memberId, wwRegistration);
    handleCancel();
  };

  // Functions to handle change of the arrival field
  const onChangeArrival = (event: SelectChangeEvent<Arrival | undefined>) => {
    setArrival(event.target.value as Arrival | undefined);
    if (!checkWWArrivalAndDeparture(event.target.value as Arrival | undefined, departure)) {
      setErrorArrivalAfterDeparture(true);
    } else {
      setErrorArrivalAfterDeparture(false);
    }
    setErrorArrival(false);
  };

  // Functions to handle change of the departure field
  const onChangeDeparture = (event: SelectChangeEvent<Departure | undefined>) => {
    setDeparture(event.target.value as Departure | undefined);
    if (!checkWWArrivalAndDeparture(arrival, event.target.value as Departure | undefined)) {
      setErrorDepartureBeforeArrival(true);
    } else {
      setErrorDepartureBeforeArrival(false);
    }
    setErrorDeparture(false);
  };

  // Functions to handle change of the car field
  const onChangeCar = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCar(event.target.value === "true");
  };

  // Functions to handle change of the seats field
  const onChangeSeats = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSeats(Number(event.target.value));
  };

  // Functions to handle change of the vegetarian field
  const onChangeVegetarian = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVegetarian(event.target.value === "true");
  };

  // Functions to handle change of the remarks field
  const onChangeRemarks = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRemarks(event.target.value);
  };

  // Functions to handle change of the debitNotice field
  const onChangeDebitNotice = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDebitNotice(event.target.checked);
  };

  if (!ww) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle fontWeight={"bold"}>Anmeldung für {ww.eventName}</DialogTitle>
      <DialogContent sx={{ ml: 1, mr: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={4} sm={2}>
            <Typography fontWeight={"bold"}>Beginn:</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography>{dayjs(ww.eventBeginn)?.format("DD.MM.YYYY")}</Typography>
          </Grid>
          <Grid item xs={4} sm={2}>
            <Typography fontWeight={"bold"}>Ende:</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography>{dayjs(ww.eventEnd)?.format("DD.MM.YYYY")}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 1, mb: 2 }} />
        <Grid container spacing={3}>
          <Grid container item spacing={1}>
            <Grid container item xs={12} sm={6}>
              <Box width={1}>
                <Typography fontWeight={"bold"}>Anreise</Typography>
                <Select variant="outlined" value={arrival} onChange={onChangeArrival} fullWidth>
                  <MenuItem value={Arrival.Ffr}>Freitag früh</MenuItem>
                  <MenuItem value={Arrival.FvM}>Freitag vor dem Mittagessen</MenuItem>
                  <MenuItem value={Arrival.FvA}>Freitag vor dem Abendessen</MenuItem>
                  <MenuItem value={Arrival.SvF}>Samstag vor dem Frühstück</MenuItem>
                  <MenuItem value={Arrival.SvM}>Samstag vor dem Mittagessen</MenuItem>
                  <MenuItem value={Arrival.SvA}>Samstag vor dem Abendessen</MenuItem>
                  <MenuItem value={Arrival.Ssp}>Samstag spät</MenuItem>
                </Select>
                {errorArrivalAfterDeparture && (
                  <FormHelperText error>Die Anreise darf nicht vor der Abreise liegen</FormHelperText>
                )}
                {errorArrival && <FormHelperText error>Bitte wähle eine Anreisezeit aus</FormHelperText>}
              </Box>
            </Grid>
            <Grid container item xs={12} sm={6}>
              <Box width={1}>
                <Typography fontWeight={"bold"}>Abreise</Typography>
                <Select variant="outlined" value={departure} onChange={onChangeDeparture} fullWidth>
                  <MenuItem value={Departure.FnM}>Freitag nach dem Mittagessen</MenuItem>
                  <MenuItem value={Departure.FnA}>Freitag nach dem Abendessen</MenuItem>
                  <MenuItem value={Departure.SnF}>Samstag nach dem Frühstück</MenuItem>
                  <MenuItem value={Departure.SnM}>Samstag nach dem Mittagessen</MenuItem>
                  <MenuItem value={Departure.SnA}>Samstag nach dem Abendessen</MenuItem>
                  <MenuItem value={Departure.Son}>Sonntag</MenuItem>
                </Select>
                {errorDepartureBeforeArrival && (
                  <FormHelperText error>Die Abreise darf nicht vor der Anreise liegen</FormHelperText>
                )}
                {errorDeparture && <FormHelperText error>Bitte wähle eine Abreisezeit aus</FormHelperText>}
              </Box>
            </Grid>
          </Grid>
          <Grid container item>
            <Grid item xs={12} sm={6}>
              <Typography fontWeight={"bold"}>Steht ein Auto zur Verfügung?</Typography>
              <FormControl>
                <RadioGroup row value={car} onChange={onChangeCar}>
                  <FormControlLabel value={true} control={<Radio />} label="Ja" />
                  <FormControlLabel value={false} control={<Radio />} label="Nein" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {car && (
              <Grid item xs={8} sm={6}>
                <Typography fontWeight={"bold"}>Anzahl freier Plätze</Typography>
                <NumberField
                  variant="outlined"
                  size="small"
                  value={seats}
                  onChange={onChangeSeats}
                  fullWidth
                  placeholder="0"
                />
                {errorSeats && <FormHelperText error>Bitte wähle eine gültige Anzahl aus</FormHelperText>}
              </Grid>
            )}
          </Grid>
          <Grid item>
            <Typography fontWeight={"bold"}>Vegetarier</Typography>
            <FormControl>
              <RadioGroup row value={vegetarian} onChange={onChangeVegetarian}>
                <FormControlLabel value={true} control={<Radio />} label="Ja" />
                <FormControlLabel value={false} control={<Radio />} label="Nein" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography fontWeight={"bold"}>Bemerkungen</Typography>
            <TextField
              variant="outlined"
              value={remarks}
              onChange={onChangeRemarks}
              fullWidth
              multiline
              maxRows={10}
              placeholder={"Alt-Trainees: Bitte nachzuholende Pflichtworkshops hier eintragen!"}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography fontWeight={"bold"}>Lastschriftverfahren</Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControl>
                <FormControlLabel
                  control={<Checkbox value={debitNotice} onChange={onChangeDebitNotice} />}
                  label="Hiermit ermächtige ich den Junior Business Team e.V., den Beitrag für das Working Weekend von meinem
                 Konto einzuziehen."
                />
                {errorDebitNotice && (
                  <FormHelperText error>Der Einzugsermächtigung muss zugestimmt werden</FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ ml: 3, mr: 3, mb: 2 }}>
        <Button variant="contained" fullWidth color="primary" onClick={handleCancel}>
          Abbrechen
        </Button>
        <Button variant="contained" fullWidth color="primary" onClick={handleSubmit}>
          Anmelden
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkingWeekendSignUpDialog;
